import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { OverflowAvatarStack } from '../OverflowAvatarStack';
import * as Styled from '../styles';
import { type ReportingLinesUser } from '../types';

import { ReportingLinesUserCardWithLine } from './shared';

interface ManagerSectionProps {
	orderedManagerList: ReportingLinesUser[];
	showAvatarStack: boolean;
	toggleAvatarStack: () => void;
	isSelectedUserManager: boolean;
}

export const ManagerSection = ({
	orderedManagerList,
	showAvatarStack,
	toggleAvatarStack,
	isSelectedUserManager,
}: ManagerSectionProps): React.JSX.Element => {
	const unstackedManagerLimit = isSelectedUserManager ? 1 : 2;
	const isT26DesignDropCoreExperiences = fg('enable_teams_t26_design_drop_core_experiences');
	const firstManager = orderedManagerList.at(-1);

	const orderedMiddleManagers = orderedManagerList.slice(unstackedManagerLimit, -1);
	const hasManagersForAvatarStack = isT26DesignDropCoreExperiences
		? orderedMiddleManagers.length > 0
		: orderedManagerList.length > unstackedManagerLimit + 1;

	return (
		<Styled.ManagersList testId="manager-section">
			{hasManagersForAvatarStack && showAvatarStack ? (
				<>
					{firstManager && (
						<Styled.ReportsListItem>
							<ReportingLinesUserCardWithLine user={firstManager} testId="manager-stack-top-card" />
						</Styled.ReportsListItem>
					)}
					<Styled.ReportsListItem>
						<Styled.ManagerAvatarStackWrapper key="manager-avatars" testId="manager-avatars">
							<OverflowAvatarStack
								atlassianUsers={
									isT26DesignDropCoreExperiences
										? orderedMiddleManagers.reverse()
										: orderedManagerList.slice(unstackedManagerLimit + 1).reverse()
								}
								numFilteredMissingProfiles={0}
								avatarSize="small"
								onClick={toggleAvatarStack}
								includeNameInMoreText
							/>
						</Styled.ManagerAvatarStackWrapper>
						<Styled.VerticalChartLine />
					</Styled.ReportsListItem>
				</>
			) : (
				orderedManagerList
					.slice(unstackedManagerLimit)
					.map((manager) => (
						<Styled.ReportsListItem key={manager.accountIdentifier ?? undefined}>
							<ReportingLinesUserCardWithLine user={manager} />
						</Styled.ReportsListItem>
					))
					.reverse()
			)}
			{isSelectedUserManager
				? null
				: orderedManagerList[1] && (
						<Styled.ReportsListItem>
							<ReportingLinesUserCardWithLine
								user={orderedManagerList[1]}
								testId="skip-manager-card"
							/>
						</Styled.ReportsListItem>
					)}
			{orderedManagerList[0] && (
				<Styled.ReportsListItem>
					<ReportingLinesUserCardWithLine
						user={orderedManagerList[0]}
						testId="direct-manager-card"
					/>
				</Styled.ReportsListItem>
			)}
		</Styled.ManagersList>
	);
};
